import { bopApi } from '@/lib/api/client';
import { withoutUndefined } from '@/lib/api/search-params';

export const ABUSE_PATH = 'v1/compliance/abuse';

export const ABUSE_SUBJECT_TYPES = ['user', 'apiKey', 'ip'] as const;
export type AbuseSubjectType = (typeof ABUSE_SUBJECT_TYPES)[number];

export const ABUSE_ALERT_STATES = ['idle', 'triggered', 'case_open', 'escalated'] as const;
export type AbuseAlertState = (typeof ABUSE_ALERT_STATES)[number];

export const ABUSE_SUBJECT_SORTS = ['peak', 'score', 'triggered'] as const;
export type AbuseSubjectSort = (typeof ABUSE_SUBJECT_SORTS)[number];

export type AbuseMode = 'live' | 'shadow';

/** The wire is already camelCase; mappers only coerce enums the API may extend before this UI ships */
export interface RawAbuseEvent {
  /** ISO-8601 UTC */
  occurredAt: string;
  eventType: string;
  weight: number;
  service: string;
  endpoint: string;
  ip: string;
  actorType: 'user' | 'apiKey';
  actorId: string;
  requestId?: string;
  detail?: Record<string, string | number | boolean>;
}

export type AbuseEvent = RawAbuseEvent;

export interface RawAbuseSubject {
  subjectType: AbuseSubjectType;
  subjectId: string;
  /** Decayed to the read time */
  score: number;
  updatedAt: string;
  alertState: string;
  peakScore: number;
  peakAt: string;
  triggeredAt?: string;
  triggeredScore?: number;
  escalatedAt?: string;
  escalatedScore?: number;
  caseAlertId?: string;
}

export interface AbuseSubject extends Omit<RawAbuseSubject, 'alertState'> {
  alertState: AbuseAlertState;
}

export interface AbuseSubjectKey {
  subjectType: AbuseSubjectType;
  subjectId: string;
}

export interface AbuseTimeRange {
  /** ISO-8601 */
  from: string;
  to: string;
}

export interface RawAbuseSummary extends AbuseTimeRange {
  mode: AbuseMode;
  scoring: { threshold: number; escalationThreshold: number; halfLifeHours: number; weights: Record<string, number> };
  totals: {
    events: number;
    /** null when the previous window reaches past event retention */
    previousEvents: number | null;
    /** Users and API keys only: an IP crossing usually shadows one of theirs */
    crossings: number;
    crossingsBySubjectType: Record<AbuseSubjectType, number>;
    activeSubjects: Record<AbuseSubjectType, number>;
  };
  daily: Array<{ date: string; events: number; crossings: number }>;
  byType: Array<{ eventType: string; weight: number; events: number }>;
  byService: Array<{ service: string; events: number }>;
}

export type AbuseSummary = RawAbuseSummary;

export interface AbuseEventsRequest extends AbuseTimeRange {
  subjectType: AbuseSubjectType;
  eventType?: string;
  service?: string;
  limit?: number;
  cursor?: string;
}

export interface RawAbuseEventsResponse extends AbuseTimeRange {
  subjectType: AbuseSubjectType;
  events: RawAbuseEvent[];
  cursor?: string;
}

export interface AbuseEventsPage {
  events: AbuseEvent[];
  cursor?: string;
}

export interface AbuseSubjectsRequest {
  subjectType: AbuseSubjectType;
  sort: AbuseSubjectSort;
  /** ISO-8601 */
  since: string;
  limit?: number;
}

export interface RawAbuseSubjectActivity extends RawAbuseSubject {
  eventCount: number;
  linkedCount: number;
  /** Both counts are lower bounds */
  isActivityCapped: boolean;
}

export interface AbuseSubjectActivity extends AbuseSubject {
  eventCount: number;
  linkedCount: number;
  isActivityCapped: boolean;
}

export interface RawAbuseSubjectsResponse {
  since: string;
  subjectType: AbuseSubjectType;
  sort: AbuseSubjectSort;
  subjects: RawAbuseSubjectActivity[];
}

export interface AbuseCase {
  alertId: string;
  status: string;
  severity: string;
  createdAt: string;
}

export interface RawAbuseSubjectDetail extends AbuseTimeRange {
  subject: RawAbuseSubject | null;
  events: RawAbuseEvent[];
  /** More events exist in the window than were returned, so `linked` may be incomplete */
  isEventsCapped: boolean;
  linked: AbuseSubjectKey[];
  case: AbuseCase | null;
}

export interface AbuseSubjectDetail extends Omit<RawAbuseSubjectDetail, 'subject'> {
  subject: AbuseSubject | null;
}

const isAlertState = (value: string): value is AbuseAlertState => (ABUSE_ALERT_STATES as readonly string[]).includes(value);

export const mapSubject = <T extends RawAbuseSubject>(raw: T): Omit<T, 'alertState'> & { alertState: AbuseAlertState } => ({
  ...raw,
  alertState: isAlertState(raw.alertState) ? raw.alertState : 'idle',
});

export const mapEventsResponse = (raw: RawAbuseEventsResponse): AbuseEventsPage => ({ events: raw.events ?? [], ...(raw.cursor ? { cursor: raw.cursor } : {}) });

export const mapSubjectsResponse = (raw: RawAbuseSubjectsResponse): AbuseSubjectActivity[] => (raw.subjects ?? []).map(mapSubject);

export const mapSubjectDetail = (raw: RawAbuseSubjectDetail): AbuseSubjectDetail => ({ ...raw, subject: raw.subject ? mapSubject(raw.subject) : null });

export const getAbuseSummary = (range: AbuseTimeRange, signal?: AbortSignal) =>
  bopApi.get(`${ABUSE_PATH}/summary`, { searchParams: withoutUndefined(range), signal }).json<RawAbuseSummary>();

export const getAbuseEvents = (request: AbuseEventsRequest, signal?: AbortSignal) =>
  bopApi.get(`${ABUSE_PATH}/events`, { searchParams: withoutUndefined(request), signal }).json<RawAbuseEventsResponse>();

export const getAbuseSubjects = (request: AbuseSubjectsRequest, signal?: AbortSignal) =>
  bopApi.get(`${ABUSE_PATH}/subjects`, { searchParams: withoutUndefined(request), signal }).json<RawAbuseSubjectsResponse>();

/** Without a range the API reads its default window (the full event retention) */
export const getAbuseSubject = (key: AbuseSubjectKey, range?: AbuseTimeRange, signal?: AbortSignal) =>
  bopApi.get(`${ABUSE_PATH}/subject`, { searchParams: withoutUndefined({ type: key.subjectType, id: key.subjectId, ...range }), signal }).json<RawAbuseSubjectDetail>();
